import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Building2, Check, ChevronDown, Search } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/cn'
import { getCompanyColor } from '../utils/companyColor'

const MENU_GAP = 6
const VIEWPORT_PADDING = 8
const PANEL_WIDTH = 340

export interface CompanyChipItem {
  _id: string
  name: string
  /** Optional admin-picked hex tint; falls back to palette hash. */
  color?: string
}

export interface CompanyChipsProps {
  companies: CompanyChipItem[]
  /** Empty set means "All companies" is selected. */
  selectedCompanyIds: Set<string>
  /** Pass `null` to clear (selects "All"). */
  onToggle: (companyId: string | null) => void
  /** Optional override for the "All" chip label. */
  allLabel?: string
  /** Suppress the "All companies" toggle entirely (single-select use cases). */
  hideAll?: boolean
}

/** Small color dot for a company, honoring an admin hex override. */
function CompanyDot({ item }: { item: CompanyChipItem }): React.ReactElement {
  const color = getCompanyColor(item._id, item.color)
  return (
    <span
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-gray-100 ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700"
      aria-hidden
    >
      <span
        className={cn('h-2 w-2 rounded-full', item.color ? '' : color.dotBg)}
        style={item.color ? { backgroundColor: color.dot } : undefined}
      />
    </span>
  )
}

function AllCompaniesIcon(): React.ReactElement {
  return (
    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-gray-100 ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700">
      <Building2 className="h-3 w-3 text-gray-500" aria-hidden />
    </span>
  )
}

/**
 * Searchable multi-select company scope selector.
 *
 * Renders a compact trigger button that summarizes the current selection
 * ("All companies" / a single company name / "N companies") and opens a
 * popover with a search box, an "All companies" toggle, and a scrollable
 * checkbox list. The panel edits a draft selection: opening in "All" mode
 * shows every company checked, unchecking one keeps the rest checked, and
 * the "All companies" tick toggles the full list both ways. The draft is
 * committed back through `onToggle` when the panel closes (Apply, Escape or
 * outside click); a fully-checked or fully-unchecked draft collapses to the
 * empty-set "All" state. Scales to any number of companies because search
 * does the filtering — no wrapping pill row.
 */
export function CompanyChips({
  companies,
  selectedCompanyIds,
  onToggle,
  allLabel = 'All companies',
  hideAll = false,
}: CompanyChipsProps): React.ReactElement {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // Panel-local draft; committed to the parent on close. In multi-select mode
  // the parent's empty set ("All") expands to every id so each row shows checked.
  const [draft, setDraft] = useState<Set<string>>(() => new Set())
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [position, setPosition] = useState<{
    top: number
    left: number
    width: number
    maxHeight: number
  } | null>(null)

  const openPanel = useCallback((): void => {
    // "All" (empty set) expands to a fully-checked draft in multi-select mode.
    setDraft(
      !hideAll && selectedCompanyIds.size === 0
        ? new Set(companies.map((c) => c._id))
        : new Set(selectedCompanyIds),
    )
    setOpen(true)
  }, [companies, hideAll, selectedCompanyIds])

  // Push the draft back to the parent as individual toggles. A fully-checked
  // or fully-unchecked draft collapses to the empty-set "All" state.
  const commitDraft = useCallback((): void => {
    if (hideAll) return
    if (draft.size === 0 || draft.size === companies.length) {
      if (selectedCompanyIds.size > 0) onToggle(null)
      return
    }
    for (const c of companies) {
      if (selectedCompanyIds.has(c._id) !== draft.has(c._id)) onToggle(c._id)
    }
  }, [hideAll, draft, companies, selectedCompanyIds, onToggle])

  const close = useCallback((): void => {
    commitDraft()
    setOpen(false)
    setQuery('')
  }, [commitDraft])

  const companyById = useMemo(() => {
    const m = new Map<string, CompanyChipItem>()
    for (const c of companies) m.set(c._id, c)
    return m
  }, [companies])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return companies
    return companies.filter((c) => c.name.toLowerCase().includes(q))
  }, [companies, query])

  const selectedCount = selectedCompanyIds.size
  const allSelected = selectedCount === 0
  // Draft-side flags drive the open panel's checkboxes.
  const allChecked = !hideAll && companies.length > 0 && draft.size === companies.length

  const toggleAllDraft = useCallback((): void => {
    setDraft((prev) =>
      prev.size === companies.length ? new Set() : new Set(companies.map((c) => c._id)),
    )
  }, [companies])

  // Trigger summary: "All companies" / single name+dot / "N companies".
  const summary = useMemo(() => {
    if (selectedCount === 0) {
      return { label: hideAll ? 'Select company' : allLabel, item: null as CompanyChipItem | null }
    }
    if (selectedCount === 1) {
      const id = Array.from(selectedCompanyIds)[0]
      const item = companyById.get(id) ?? null
      return { label: item?.name ?? '1 company', item }
    }
    return { label: `${selectedCount} companies`, item: null }
  }, [selectedCount, hideAll, allLabel, selectedCompanyIds, companyById])

  const updatePosition = useCallback((): void => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const panelHeight = panelRef.current?.offsetHeight ?? 0
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_PADDING
    const spaceAbove = rect.top - MENU_GAP - VIEWPORT_PADDING
    const openUp = panelHeight > 0 && spaceBelow < panelHeight && spaceAbove > spaceBelow
    const maxHeight = Math.max(200, openUp ? spaceAbove : spaceBelow)
    const top = openUp
      ? Math.max(VIEWPORT_PADDING, rect.top - MENU_GAP - Math.min(panelHeight, maxHeight))
      : rect.bottom + MENU_GAP
    const width = Math.max(rect.width, PANEL_WIDTH)
    const left = Math.min(
      Math.max(VIEWPORT_PADDING, rect.left),
      window.innerWidth - VIEWPORT_PADDING - width,
    )
    setPosition((current) => {
      const next = { top, left, width, maxHeight }
      if (
        current?.top === next.top &&
        current.left === next.left &&
        current.width === next.width &&
        current.maxHeight === next.maxHeight
      ) {
        return current
      }
      return next
    })
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open, position, updatePosition])

  useEffect(() => {
    if (!open) return
    // Focus the search box once the panel mounts.
    searchRef.current?.focus()
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close()
    }
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      close()
    }
    document.addEventListener('keydown', handleEscape)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, close])

  const handleRowClick = (id: string): void => {
    // Single-select mode acts like a picker: choose and close.
    if (hideAll) {
      onToggle(id)
      setOpen(false)
      setQuery('')
      return
    }
    setDraft((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close() : openPanel())}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'box-border inline-flex h-9 min-w-0 max-w-[18rem] cursor-pointer items-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm transition-all duration-150 hover:border-gray-300 hover:bg-gray-50 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-gray-600 dark:hover:bg-gray-700/70',
        )}
      >
        {summary.item ? (
          <CompanyDot item={summary.item} />
        ) : (
          <AllCompaniesIcon />
        )}
        <span className="min-w-0 flex-1 truncate text-left font-medium">{summary.label}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-gray-400 transition-transform duration-150',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            aria-multiselectable={!hideAll}
            className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
              zIndex: 9999,
            }}
          >
            {/* Search */}
            <div className="border-b border-gray-100 p-2.5 dark:border-gray-700">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search companies"
                  aria-label="Search companies"
                  className="box-border h-10 w-full rounded-xl border border-gray-200 bg-white/95 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:placeholder:text-gray-500"
                />
              </div>
              {/* presets slot — saved segment chips drop in here (deferred). */}
            </div>

            {/* All companies toggle */}
            {!hideAll && (
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-700">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={allChecked ? true : draft.size > 0 ? 'mixed' : false}
                  onClick={toggleAllDraft}
                  className="flex flex-1 cursor-pointer items-center gap-2.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                      allChecked
                        ? 'border-primary bg-primary text-white'
                        : draft.size > 0
                          ? 'border-primary bg-primary/10 text-primary dark:bg-primary/20'
                          : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800',
                    )}
                  >
                    {allChecked ? (
                      <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
                    ) : draft.size > 0 ? (
                      <span className="h-0.5 w-2 rounded-full bg-primary" aria-hidden />
                    ) : null}
                  </span>
                  {allLabel}
                </button>
                {draft.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setDraft(new Set())}
                    className="cursor-pointer text-sm font-medium text-primary hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* Company list */}
            <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
              {filtered.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  No companies found
                </p>
              ) : (
                filtered.map((c) => {
                  const active = hideAll ? selectedCompanyIds.has(c._id) : draft.has(c._id)
                  return (
                    <button
                      key={c._id}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => handleRowClick(c._id)}
                      className={cn(
                        'flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
                        'text-gray-700 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700/60',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center border transition-colors',
                          hideAll ? 'rounded-full' : 'rounded',
                          active
                            ? 'border-primary bg-primary text-white'
                            : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800',
                        )}
                      >
                        {active && (
                          hideAll ? (
                            <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden />
                          ) : (
                            <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
                          )
                        )}
                      </span>
                      <CompanyDot item={c} />
                      <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
                    </button>
                  )
                })
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2.5 dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {hideAll
                  ? allSelected
                    ? 'Select company'
                    : `${selectedCount} selected`
                  : allChecked
                    ? allLabel
                    : `${draft.size} selected`}
              </span>
              <Button type="button" variant="primary" size="sm" onClick={close}>
                Apply
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

CompanyChips.displayName = 'CompanyChips'
